import type {
  AdminHardwareDetail,
  AdminHardwareListItem,
  AdminHardwarePair,
} from "@/types/admin-hardware";
import type { MockUserId } from "@/lib/api/mock-data/users.mock";

const FW_MIN = "2.4.0";
/** A version known to be below FW_MIN — used by firmware-compliance tests. */
export const BELOW_MIN_FW = "2.3.7";

const dev = (
  partial: Pick<AdminHardwareDetail, "serial" | "status" | "boundUserId"> &
    Partial<AdminHardwareDetail>,
): AdminHardwareDetail => ({
  serial: partial.serial,
  status: partial.status,
  boundUserId: partial.boundUserId,
  boundAt: partial.boundAt ?? null,
  orderId: partial.orderId ?? null,
  firmwareVersion: partial.firmwareVersion ?? "2.6.1",
  firmwareMinSupported: FW_MIN,
  batteryPct: partial.batteryPct ?? 82,
  lastSeenAt: partial.lastSeenAt ?? null,
  refurbished: partial.refurbished ?? false,
  shippingCarrier: partial.shippingCarrier ?? null,
  shippingTrackingNumber: partial.shippingTrackingNumber ?? null,
  blacklisted: partial.blacklisted ?? false,
  hardwareGeneration: partial.hardwareGeneration ?? "v1",
});

export const MOCK_HARDWARE: Record<MockUserId, AdminHardwarePair> = {
  u_001_alice_active: {
    userId: "u_001_alice_active",
    primary: dev({
      serial: "ZB1-A001-77F2",
      status: "active",
      boundUserId: "u_001_alice_active",
      boundAt: "2024-08-18T12:00:00Z",
      lastSeenAt: "2026-05-19T20:11:00Z",
      orderId: "ord_001_alice",
    }),
    history: [],
  },
  u_002_bob_trial_pending: {
    userId: "u_002_bob_trial_pending",
    primary: dev({
      serial: "ZB1-B002-A41C",
      status: "shipping",
      boundUserId: "u_002_bob_trial_pending",
      boundAt: null,
      lastSeenAt: null,
      batteryPct: null,
      firmwareVersion: null,
      shippingCarrier: "PostNord",
      shippingTrackingNumber: "PN1234567890SE",
      orderId: "ord_002_bob",
    }),
    history: [],
  },
  u_003_cara_trial_ending: {
    userId: "u_003_cara_trial_ending",
    primary: dev({
      serial: "ZB1-C003-19B0",
      status: "active",
      boundUserId: "u_003_cara_trial_ending",
      boundAt: "2026-05-02T10:00:00Z",
      lastSeenAt: "2026-05-20T07:33:00Z",
      batteryPct: 64,
      orderId: "ord_003_cara",
    }),
    history: [],
  },
  u_004_dave_card_failed: {
    userId: "u_004_dave_card_failed",
    primary: dev({
      serial: "ZB1-D004-66E1",
      status: "active",
      boundUserId: "u_004_dave_card_failed",
      boundAt: "2026-04-26T10:00:00Z",
      lastSeenAt: "2026-05-09T22:01:00Z",
      batteryPct: 21,
      // Firmware below FW_MIN — exercises the firmware-compliance branch.
      firmwareVersion: BELOW_MIN_FW,
      orderId: "ord_004_dave",
    }),
    history: [],
  },
  u_005_erin_charged_off: {
    userId: "u_005_erin_charged_off",
    primary: null,
    history: [
      dev({
        serial: "ZB1-E005-23A5",
        status: "charged_off",
        boundUserId: "u_005_erin_charged_off",
        boundAt: "2026-02-18T10:00:00Z",
        lastSeenAt: "2026-04-02T13:20:00Z",
        orderId: "ord_005_erin",
      }),
    ],
  },
  u_006_finn_past_due: {
    userId: "u_006_finn_past_due",
    primary: dev({
      serial: "ZB1-F006-90DD",
      status: "active",
      boundUserId: "u_006_finn_past_due",
      boundAt: "2025-09-12T10:00:00Z",
      lastSeenAt: "2026-05-15T19:00:00Z",
      batteryPct: 71,
      orderId: "ord_006_finn",
    }),
    history: [],
  },
  u_007_gina_suspended: {
    userId: "u_007_gina_suspended",
    primary: null,
    history: [
      dev({
        serial: "ZB1-G007-44CB",
        status: "lost",
        boundUserId: "u_007_gina_suspended",
        boundAt: "2025-11-25T10:00:00Z",
        lastSeenAt: "2026-03-14T10:05:00Z",
        blacklisted: true,
        orderId: "ord_007_gina",
      }),
    ],
  },
  u_008_hugo_cancelled: {
    userId: "u_008_hugo_cancelled",
    primary: dev({
      serial: "ZB1-H008-E2F8",
      status: "active",
      boundUserId: "u_008_hugo_cancelled",
      boundAt: "2024-12-08T10:00:00Z",
      lastSeenAt: "2026-05-10T18:30:00Z",
      batteryPct: 88,
      orderId: "ord_008_hugo",
    }),
    history: [],
  },
};

/**
 * Unbound / extra devices that don't belong to a user but should appear in
 * the global hardware list. Covers replaced, serial_mismatch, and
 * firmware_below_min status varieties that no single user fixture exposes.
 */
export const MOCK_UNBOUND_HARDWARE: AdminHardwareDetail[] = [
  dev({
    serial: "ZB1-R009-AA01",
    status: "replaced",
    boundUserId: null,
    boundAt: "2025-06-01T10:00:00Z",
    lastSeenAt: "2026-01-10T08:00:00Z",
    orderId: "ord_009_legacy",
    refurbished: true,
  }),
  dev({
    serial: "ZB1-X010-BB02",
    status: "serial_mismatch",
    boundUserId: null,
    boundAt: null,
    lastSeenAt: null,
    orderId: "ord_010_qa",
  }),
  dev({
    serial: "ZB1-F011-CC03",
    status: "firmware_below_min",
    boundUserId: null,
    boundAt: "2025-02-12T10:00:00Z",
    lastSeenAt: "2025-12-04T09:00:00Z",
    firmwareVersion: BELOW_MIN_FW,
    orderId: "ord_011_legacy",
  }),
];

function toListItem(d: AdminHardwareDetail): AdminHardwareListItem {
  return {
    serial: d.serial,
    status: d.status,
    boundUserId: d.boundUserId,
    orderId: d.orderId,
    firmwareVersion: d.firmwareVersion,
    firmwareMinSupported: d.firmwareMinSupported,
    blacklisted: d.blacklisted,
    lastSeenAt: d.lastSeenAt,
    hardwareGeneration: d.hardwareGeneration,
  };
}

/**
 * Flattened list rows across every fixture device (primary + history +
 * unbound). Used by listAdminHardware so the dashboard does not have to
 * fan out per-user calls.
 */
export const MOCK_HARDWARE_LIST: AdminHardwareListItem[] = [
  ...Object.values(MOCK_HARDWARE).flatMap((pair) => {
    const items: AdminHardwareDetail[] = [];
    if (pair.primary) items.push(pair.primary);
    items.push(...pair.history);
    return items;
  }),
  ...MOCK_UNBOUND_HARDWARE,
].map(toListItem);
